import { Trans } from '@lingui/react/macro';
import { type ReactNode, useCallback, useEffect } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';

import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';

type Props = {
  message: string;
  shouldPreventNavigation: boolean;
  stayText?: ReactNode;
  leaveText?: ReactNode;
  onStay?: () => void;
  onLeave?: () => void;
  onBeforeUnload?: () => void;
};

const Prompt = ({
  message,
  shouldPreventNavigation,
  stayText = <Trans>Keep Editing</Trans>,
  leaveText = <Trans>Exit</Trans>,
  onStay,
  onLeave,
  onBeforeUnload,
}: Props) => {
  const blocker = useBlocker(shouldPreventNavigation);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldPreventNavigation) {
        e.preventDefault();
        e.returnValue = message;
      }

      onBeforeUnload?.();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  });

  const handleStay = useCallback(() => {
    onStay?.();
    blocker.reset?.();
  }, [blocker, onStay]);

  const handleLeave = useCallback(() => {
    onLeave?.();
    blocker.proceed?.();
  }, [blocker, onLeave]);

  useBeforeUnload(handleStay);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleStay();
    }
  };

  return (
    <Dialog open={blocker.state === 'blocked'} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{message}</DialogTitle>
        </DialogHeader>
        <DialogFooter>
          <DialogClose render={<Button size="large">{stayText}</Button>} />
          <Button size="large" variant="on-primary" onClick={handleLeave}>
            {leaveText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Prompt;
